"use client";

import { AlgoliaEvents, AlgoliaInsightsMiddleware, getAlgoliaClient, useAlgoliaIndex } from "@entities/algolia";
import { Vacancy, VacancyableType, VacancyCard } from "@entities/vacancy";
import { useClientConfig } from "@providers/app-provider/client-app-provider";
import { EmptyScreen, TVacancyCard } from "@shared/cms";
import { Pagination } from "@shared/types";
import { toCamel } from "@shared/utils";
import { SharedPagination, useIsMobile } from "@ubud-ui/react-ui-kit";
import { AlgoliaHit } from "instantsearch.js";
import { useRouter, useSearchParams } from "next/navigation";
import qs from "qs";
import { useMemo } from "react";
import { Configure, InstantSearch, useHits } from "react-instantsearch";
import { VacancyListGridConfig } from "../../types";

type VacancyRouteState = {
  keyword?: string;
  page?: number;
  locations?: Array<{
    id: string;
    type: "city" | "province";
    label?: string;
  }>;
  jobTypes?: Array<{
    id: string;
    label?: string;
  }>;
  educations?: Array<{
    id: string;
    label?: string;
  }>;
  platforms?: Array<{
    id: string;
    label?: string;
  }>;
  jobFunctions?: Array<{
    id: string;
    label?: string;
  }>;
  industries?: Array<{
    id: string;
    label?: string;
  }>;
  skills?: Array<{
    id: string;
    label?: string;
  }>;
  physicals?: Array<{
    id: string;
    label?: string;
  }>;
  disabilities?: Array<{
    id: string;
    label?: string;
  }>;
};

type VacancyHit = AlgoliaHit<Vacancy>;

function Hits({ config }: { config: VacancyListGridConfig }) {
  const { items, sendEvent } = useHits<VacancyHit>();

  const handleClick = (vacancy: VacancyHit) => {
    if (vacancy) {
      sendEvent(
        "click",
        vacancy as any,
        vacancy.vacancyableType === VacancyableType.MICRO_VACANCY
          ? AlgoliaEvents.MICRO_VACANCY_CLICKED
          : AlgoliaEvents.INDUSTRIAL_VACANCY_CLICKED,
      );
    }
  };

  if (items.length <= 0) {
    return <EmptyScreen {...config.emptyScreen} />;
  }

  return (
    <>
      {items.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => {
            const vacancy = toCamel(item);

            return (
              <VacancyCard
                meta={config.vacancyCard as TVacancyCard}
                vacancy={vacancy}
                key={vacancy.id}
                onClick={() => handleClick(vacancy)}
              />
            );
          })}
        </div>
      ) : (
        <EmptyScreen {...config.emptyScreen} />
      )}
    </>
  );
}

function Result({ config, path }: { config: VacancyListGridConfig; path: string }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const isMobile = useIsMobile();

  const { results } = useHits<VacancyHit>();

  const pagination = useMemo(() => {
    if (results) {
      return {
        lastPage: results.nbPages,
        currentPage: results.page + 1,
        perPage: results.hitsPerPage,
      } as Pagination;
    }

    return null;
  }, [results]);

  const handlePageChange = (page: number) => {
    const currentQuery = qs.parse(searchParams.toString());

    router.push(
      `${path}?${qs.stringify(
        {
          ...currentQuery,
          page: page === pagination?.lastPage ? pagination?.lastPage : page + 1,
        },
        { skipNulls: true },
      )}`,
    );
  };

  return (
    <>
      <Hits config={config} />
      {pagination && pagination.lastPage > 1 && (
        <div className="pt-10 sm:pt-14 flex justify-center">
          <SharedPagination
            pageToShow={5}
            currentPage={pagination.currentPage - 1}
            pageCount={pagination.lastPage}
            paginationType={isMobile ? "text" : "numeric"}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );
}

export function AlgoliaVacancyListGrid({
  config,
  path = "/lowongan-dalam-negeri/lowongan",
  initialFilters,
}: {
  config: VacancyListGridConfig;
  path?: string;
  initialFilters?: any;
}) {
  const { industrialVacancy } = useAlgoliaIndex();

  const searchParams = useSearchParams();

  const clientConfig = useClientConfig();
  const user = clientConfig?.user;
  const algoliaConfig = clientConfig?.config?.algolia;

  const searchClient = getAlgoliaClient(algoliaConfig);

  const routeState = useMemo(() => {
    return qs.parse(searchParams.toString(), {
      ignoreQueryPrefix: true,
    }) as VacancyRouteState;
  }, [searchParams]);

  const facetFilters = useMemo(() => {
    const facets = [];

    if (routeState.locations) {
      facets.push(routeState.locations.map((loc) => `locations:${loc.type}:${loc.id}`));
    }

    if (routeState.jobTypes) {
      facets.push(routeState.jobTypes.map((jobType) => `job_type_id:${jobType.id}`));
    }

    if (routeState.educations) {
      facets.push(routeState.educations.map((education) => `education_id:${education.id}`));
    }

    if (routeState.platforms) {
      facets.push(
        routeState.platforms.map((platform) => {
          if (platform.id === "internal") {
            return `platform_type:internal`;
          }

          return `platform_id:${platform.id}`;
        }),
      );
    }

    if (routeState.jobFunctions) {
      facets.push(routeState.jobFunctions.map((jobFunction) => `job_function_id:${jobFunction.id}`));
    }

    if (routeState.industries) {
      facets.push(routeState.industries.map((industry) => `industry_id:${industry.id}`));
    }

    if (routeState.skills) {
      facets.push(routeState.skills.map((skill) => `skill_keys:${skill.id}`));
    }

    if (routeState.physicals) {
      facets.push(routeState.physicals.map((physical) => `physical_condition:${physical.id}`));
    }

    if (routeState.disabilities) {
      facets.push(routeState.disabilities.map((disability) => `forbidden_disability_keys:-${disability.id}`));
    }

    if (initialFilters) {
      if (initialFilters.employers) {
        facets.push(initialFilters.employers.map((employer: string) => `employer_id:${employer}`));
      }
    }

    return facets;
  }, [
    routeState.locations,
    routeState.jobTypes,
    routeState.educations,
    routeState.platforms,
    routeState.jobFunctions,
    routeState.industries,
    routeState.skills,
    routeState.physicals,
    routeState.disabilities,
    initialFilters,
  ]);

  const optionalFilters = useMemo(() => {
    const facets = [];

    const cityId = user?.profile?.domicileRegion?.regionable?.subDistrict?.cityId;
    if (cityId) {
      facets.push([`locations:city:${cityId}`]);
    }

    const skills = user?.profile?.skills;
    if (skills && skills?.length > 0) {
      facets.push(skills.map((skill) => `skill_keys:${skill.key}`));
    }

    return facets;
  }, [user]);

  const page = useMemo(() => {
    return routeState.page ? routeState.page - 1 : 0;
  }, [routeState.page]);

  const keyword = useMemo(() => {
    return routeState.keyword;
  }, [routeState.keyword]);

  if (!searchClient) {
    return <EmptyScreen {...config.emptyScreen} />;
  }

  return (
    <InstantSearch
      future={{
        preserveSharedStateOnUnmount: true,
      }}
      searchClient={searchClient}
      indexName={industrialVacancy}
    >
      <Configure
        hitsPerPage={18}
        page={page}
        query={keyword}
        facetFilters={facetFilters}
        optionalFilters={optionalFilters}
        enablePersonalization={!!user}
        userToken={user?.nacoUserId}
      />
      <Result config={config} path={path} />
      <AlgoliaInsightsMiddleware />
    </InstantSearch>
  );
}
